import { isCarbonOffsetType, isCarbonRemovalType, isSubProfileType, isTreeType, TileVariant, TileSource } from '..';
import { CarbonOffset, CarbonOffsetType } from '../carbon-offsets';
import { CarbonRemoval, CarbonRemovalType } from '../carbon-removal';
import { HabitatRestoration, HabitatRestorationType } from '../habitat-restoration';
import { ImpactType } from '../impact/units';
import { PublicProjectSummary } from '../projects';
import { SubProfileTileType, Tree, TreeType } from '../tree';
import { isHabitatRestorationType } from './type-guards';

/**
 * An instance of an impact class
 */
export type Impact = CarbonOffset | CarbonRemoval | HabitatRestoration | Tree;

/**
 * The type matching the instance of an impact class
 */
export type ImpactTile = CarbonOffsetType | CarbonRemovalType | HabitatRestorationType | TreeType;

export type NonImpactTile = SubProfileTileType;
export type Tile = ImpactTile | NonImpactTile;

/**
 * This is a type for the enum values of the impact types
 */
export type TileType = ImpactType | NonImpactType;

export enum NonImpactType {
  SUBPROFILE = 'subprofile',
}

export const SUBPROFILE_TILE_PROJECT_ID = -1;

/**
 * Returns the enum value of the tile type
 */
export const getTileType = (tile: Tile, project?: PublicProjectSummary): TileType => {
  if (isSubProfileType(tile)) return NonImpactType.SUBPROFILE;

  if (isCarbonRemovalType(tile, project)) return ImpactType.CARBON_REMOVAL;
  if (isCarbonOffsetType(tile, project)) return ImpactType.CARBON_OFFSETS;
  if (isHabitatRestorationType(tile)) {
    return ImpactType.HABITAT_RESTORATION;
  }
  if (isTreeType(tile)) return ImpactType.TREES;

  throw new Error('Unable to determine tile type from tile');
};

/**
 * Returns the class instance of the impact
 */
export function getImpactInstance<T extends Impact>(tile: Tile, project?: PublicProjectSummary): T {
  if (isSubProfileType(tile))
    return new Tree({
      ...tile,
      impactType: ImpactType.TREES,
      treeNumber: 0,
      projectId: SUBPROFILE_TILE_PROJECT_ID,
      style: '',
      createdAt: new Date().toISOString(),
      variant: TileVariant.SUBPROFILE,
      source: TileSource.ECOLOGI_FEATURE,
    }) as T;

  if (isCarbonRemovalType(tile, project)) return new CarbonRemoval(tile, project) as T;

  if (isCarbonOffsetType(tile, project)) return new CarbonOffset(tile, project) as T;

  if (isHabitatRestorationType(tile)) return new HabitatRestoration(tile, project) as T;

  if (isTreeType(tile)) return new Tree(tile, project) as T;

  throw new Error('Tile type is unsupported');
}
